/**
 * Never message the person who died (T-210). Mirrors
 * `deceased-recipient.util.ts` in nestjs, which the send guard uses.
 *
 * The case's main contact counts as the deceased once a date of death is
 * entered and they are `deceased_client_id`, or a second record of the same
 * person marked « Self » / « Moi-même » (case 915). Before a date of death a
 * pre-need planner is their own main contact and that's fine.
 */

export const DECEASED_PRIMARY_WARNING = 'Primary contact is the deceased — choose a family contact';

type Fields = {
  deceased_client_id?: unknown;
  primary_client_id?: unknown;
  primary_client_relationship?: string | null;
  date_time_of_death?: unknown;
};

const SELF_WORDS = new Set(['self', 'myself', 'me', 'moi', 'moimeme', 'soimeme', 'luimeme', 'ellememe']);

const toId = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export function isSelfRelationship(relationship: string | null | undefined): boolean {
  const key = String(relationship ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[\s\-_.]/g, '');
  return key !== '' && SELF_WORDS.has(key);
}

/** True when the send guard would skip this client on this case. */
export function isDeceasedRecipient(clientId: unknown, f: Fields | null | undefined): boolean {
  const id = toId(clientId);
  if (!id || !f?.date_time_of_death) return false;
  if (id === toId(f.deceased_client_id)) return true;
  return id === toId(f.primary_client_id) && isSelfRelationship(f.primary_client_relationship);
}

export function primaryContactIsDeceased(f: Fields | null | undefined): boolean {
  return isDeceasedRecipient(f?.primary_client_id, f);
}

/**
 * At fruition the person has died even if the date isn't entered yet: the
 * deceased and a « Self » main contact can't be picked as the new main contact.
 */
export function canBeFruitionPrimary(clientId: unknown, f: Fields | null | undefined): boolean {
  return !isDeceasedRecipient(clientId, { ...f, date_time_of_death: f?.date_time_of_death || 'at fruition' });
}
